<template>
  <div class="worldview-form">
    <div class="form">
      <text-field
        :label="$t('createWorldViewPage.nameLabel')"
        :placeholder="$t('createWorldViewPage.namePlaceholder')"
        :maxlength="50"
        v-on:input="change"
        v-model="name"
      />
      <template v-for="item in detail">
        <text-field
          :label="item._label"
          :placeholder="item._placeholder"
          :maxlength="2000"
          v-on:input="change"
          v-model="item.value"
          :key="item.name"
          :rows="item._rows || 1"
        />
      </template>
      <p class="premium-label">
        <img src="@/assets/img/icon/premium.png" width="18" />
        <span>{{ $t('createWorldViewPage.addCustomFieldLabel') }}</span>
      </p>
      <div class="additional-column">
        <template v-for="(item, index) in additionalColumn">
          <div class="additional-content" :key="index" :id="index">
            <div class="remove-button" v-show="index != additionalColumn.length - 1">
              <img src="@/assets/img/icon/remove.png" width="18" @click="remove(index)" />
            </div>
            <text-field
              :label="$t('createWorldViewPage.customFieldLabel')"
              :placeholder="$t('createWorldViewPage.customFieldPlaceholder')"
              :maxlength="30"
              :disabled="plan === 0"
              @input="change"
              v-model="item.label"
              :id="`label-${item.name}`"
              :key="`label-${item.name}`"
              :ref="`label-${item.name}`"
            />
            <text-field
              :label="$t('createWorldViewPage.customFieldContentLabel')"
              :placeholder="$t('createWorldViewPage.customFieldContentPlaceholder')"
              :disabled="plan === 0"
              @input="change"
              v-model="item.content"
              :rows="4"
              :id="`content-${item.name}`"
              :key="`content-${item.name}`"
              :ref="`content-${item.name}`"
            />
          </div>
        </template>
        <div class="button-center">
          <button class="button" @click="add()" :disabled="plan === 0">{{ $t('createWorldViewPage.addMoreFieldsButton') }}</button>
        </div>
        <premium-information v-if="plan === 0" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import gql from "graphql-tag";
import { callApi } from "@/lib/api";
import TextField from "@/components/molecules/TextField.vue";
import PremiumInformation from "@/components/atoms/PremiumInformation.vue";
import { Plan } from "@/lib/models";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { TextField, PremiumInformation },
  props: {
    novelId: String,
    value: Object,
  },
  created() {
    if (!this.value) {
      return;
    }
    this.name = this.value.name;
    const { detail } = this.value as { detail: any[] };
    if (!detail) {
      return;
    }
    this.detail = this.detail.map((x) => {
      let targetValue = x.value;
      const target = detail.find((y) => y.name === x.name);
      if (target) targetValue = target.value;
      return { ...x, value: targetValue };
    });
    const { additionalColumn } = this.value as { additionalColumn: any[] };
    if (!additionalColumn || additionalColumn.length === 0) {
      return;
    }
    this.additionalColumn = additionalColumn;
  },
  async mounted() {
    const { billingInfo } = await callApi(gql`
      query BillingInfo {
        billingInfo {
          plan
        }
      }
    `);
    this.plan = billingInfo.plan;
  },
  methods: {
    change() {
      const { name, detail, additionalColumn } = this;
      this.$emit("input", {
        name,
        detail: detail.filter(({ value }) => value).map(({ name, value }) => ({ name, value })),
        additionalColumn,
      });
      this.setModified(true);
    },
    add() {
      const { additionalColumn } = this;
      additionalColumn.push({
        name: this.generateUniqueValue(),
        label: "",
        content: "",
      });
      this.change();
    },
    remove(index) {
      const { additionalColumn } = this;
      additionalColumn.splice(index, 1);
      this.change();
    },
    generateUniqueValue() {
      const N = 16;
      const bytes = new Uint8Array(N);
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
      } else {
        for (let i = 0; i < bytes.length; i += 1) {
          bytes[i] = Math.floor(Math.random() * 256);
        }
      }
      const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
      return btoa(binary).substring(0, N);
    },
  },
  data() {
    return {
      plan: 0,
      name: "",
      detail: [
        {
          name: "period",
          value: "",
          _label: this.$t('createWorldViewPage.periodLabel'),
          _placeholder: this.$t('createWorldViewPage.periodPlaceholder'),
        },
        {
          name: "law",
          value: "",
          _label: this.$t('createWorldViewPage.lawLabel'),
          _placeholder: this.$t('createWorldViewPage.lawPlaceholder'),
          _rows: 4,
        },
        {
          name: "politics",
          value: "",
          _label: this.$t('createWorldViewPage.politicsLabel'),
          _placeholder: this.$t('createWorldViewPage.politicsPlaceholder'),
          _rows: 4,
        },
        {
          name: "military",
          value: "",
          _label: this.$t('createWorldViewPage.militaryLabel'),
          _placeholder: this.$t('createWorldViewPage.militaryPlaceholder'),
          _rows: 4,
        },
        {
          name: "religion",
          value: "",
          _label: this.$t('createWorldViewPage.religionLabel'),
          _placeholder: this.$t('createWorldViewPage.religionPlaceholder'),
          _rows: 4,
        },
        {
          name: "language",
          value: "",
          _label: this.$t('createWorldViewPage.languageLabel'),
          _placeholder: this.$t('createWorldViewPage.languagePlaceholder'),
          _rows: 4,
        },
        {
          name: "other",
          value: "",
          _label: this.$t('createWorldViewPage.otherLabel'),
          _placeholder: this.$t('createWorldViewPage.otherPlaceholder'),
          _rows: 4,
        },
      ],
      additionalColumn: [
        {
          name: (this as any).generateUniqueValue(),
          label: "",
          content: "",
        },
      ],
    };
  },
});

interface Props {
  novelId: string;
  value: {
    name: string;
    detail: any[];
    additionalColumn: any[];
  };
}

interface Data {
  plan: Plan;
  name: string;
  detail: any[];
  additionalColumn: any[];
}

interface Computed {}

interface Methods {
  change(): void;
  add(): void;
  remove(index: number): void;
  generateUniqueValue(): string;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.worldview-form {
  display: flex;
  padding: 25px;

  @include mq(sp) {
    padding: 10px;
  }
}

.form {
  width: 100%;

  // フォーム要素間に等間隔の余白を追加
  ::v-deep .text-field {
    margin-bottom: 20px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  @include mq(sp) {
    width: calc(100% - 10px);
    margin-bottom: 30px;
  }
}

.additional-column {
  position: relative;
  border-top: 3px solid var(--color-primary);
  border-bottom: 3px solid var(--color-primary);
}

.additional-content {
  margin-top: 15px;
  border-bottom: 3px solid var(--color-border);
}

.remove-button {
  position: relative;
  text-align: right;

  img {
    position: absolute;
    top: -8px;
    right: 5px;
  }
}

.button-center {
  text-align: center;

  .button {
    position: relative;
    color: var(--color-primary);
    border-color: var(--color-primary);
    background-color: transparent;

    @include hover-overlay;

    &:disabled {
      color: var(--color-inactive);
      border-color: var(--color-border);
      background-color: transparent;
      &::after {
        display: none;
      }
    }

    @include mq(sp) {
      margin-top: 15px;
    }
  }
}

.button {
  margin: 0;
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 15px 150px;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);

  @include mq(sp) {
    margin: 15px 30px;
    margin-top: 0;
    padding: 15px 30px;
    width: auto;
  }
}

.premium-label {
  margin: 20px 0 3px 0;
  color: var(--color-text-normal);

  img {
    margin-right: 3px;
    vertical-align: middle;
  }

  span {
    vertical-align: middle;
    color: var(--color-text-normal);
  }
}
</style>
